import React from 'react';
import { ROOT_FOLDER_ID } from 'src/app/documents/documentConstants';

/** Mirrors Mantine's `TreeExpandedState`, which the package does not re-export. */
export type ExpandedFolderState = Record<string, boolean>;

export interface IExpandableFolder {
  id: string;
  children?: IExpandableFolder[] | undefined;
}

const collectIds = (folders: IExpandableFolder[]): string[] =>
  folders.flatMap((folder) => [
    folder.id,
    ...collectIds(folder.children ?? []),
  ]);

/** @registry Drives a Tree controller's expanded state for the document folder trees: the root and every folder start expanded, but each folder is auto-expanded only the first time it is seen, so a manual collapse survives later refreshes. */
export const useExpandedFolderState = (
  folders: IExpandableFolder[],
  setExpandedState: React.Dispatch<React.SetStateAction<ExpandedFolderState>>,
): void => {
  const seen = React.useRef(new Set<string>());

  // Mantine's own `initialize` collapses nodes it has not seen, so this has to
  // run as a parent effect after it.
  React.useEffect(() => {
    const fresh = [ROOT_FOLDER_ID, ...collectIds(folders)].filter(
      (id) => !seen.current.has(id),
    );
    if (fresh.length === 0) return;

    fresh.forEach((id) => seen.current.add(id));
    setExpandedState((current) => ({
      ...current,
      ...Object.fromEntries(fresh.map((id) => [id, true])),
    }));
  }, [folders, setExpandedState]);
};
